import { getSoqlMetaBundle } from 'common/visualizations/dataProviders/getSoqlMetaBundle';
import { getDatasetUid } from 'common/visualizations/helpers/VifSelectors';
import { whereClauseFilteringOwnColumn } from 'common/visualizations/helpers/SoqlHelpers';
import { Vif } from 'common/visualizations/vif';
import { composeQuery, select, ref, orderBy } from 'common/soql_builder';
import { I18n } from 'common/i18n';
import { QueryParts } from 'common/soql_builder/query/build';

const scope = 'shared.visualizations.charts.common';

/**
 * Get distinct values for a column (raw data, no transformations)
 * @param vif - The VIF object containing dataset information
 * @param columnName - The name of the column to get distinct values for
 * @param whereClause - Optional where clause to filter the results
 * @returns Promise that resolves to array of distinct string values (may contain nulls)
 */
export const getDistinctColumnValues = async (
  vif: Vif,
  columnName: string,
  whereClause?: string,
): Promise<string[]> => {
  try {
    // Get the SoQL data provider from the VIF
    const { soqlDataProvider } = getSoqlMetaBundle(vif);
    const datasetUid = getDatasetUid(vif);

    if (!datasetUid) {
      console.warn('No dataset UID found in VIF');
      return [];
    }

    const buildQuery = () => {
      const queryParts: QueryParts = {
        selects: [select(ref(columnName), 'distinct_value')],
        distinct: true,
        orders: [orderBy(columnName, 'ascending')],
      };

      // Add where clause if provided
      if (whereClause && whereClause.trim()) {
        queryParts.legacyWhereClause = whereClause;
      }

      return composeQuery(queryParts);
    };

    const results = await soqlDataProvider.invokeSoqlQuery(buildQuery, [], datasetUid);

    // Extract the raw values and convert to strings (preserving nulls)
    return results.map((row: any) => {
      const value = row.distinct_value;
      return value === null || value === undefined ? null : String(value);
    });
  } catch (error) {
    console.error('Error fetching distinct column values:', error);
    // Return empty array on error to gracefully handle failures
    return [];
  }
};

/**
 * Get autocomplete values for a specific column in the context of formula editing
 * This function wraps getDistinctColumnValues and transforms nulls to '(No Value)'
 * @param vif - The VIF object containing dataset information
 * @param columnName - The name of the column to get autocomplete values for
 * @returns Promise that resolves to array of autocomplete suggestion strings
 */
export const getAutocompleteValuesForColumn = async (vif: Vif, columnName: string): Promise<string[]> => {
  if (!columnName) {
    return [];
  }

  try {
    // Extract filter clause from VIF to apply existing filters
    const filterClause = whereClauseFilteringOwnColumn(vif, 0);

    // Get raw distinct values using optimized query with filters applied
    const distinctValues = await getDistinctColumnValues(vif, columnName, filterClause);

    // Transform null/empty values to '(No Value)' and remove duplicates
    const processedValues = Array.from(
      new Set(
        distinctValues.map((value) => {
          return value === null || value === undefined || value === ''
            ? I18n.t('summary_table.no_value', { scope })
            : value;
        }),
      ),
    );

    return processedValues;
  } catch (error) {
    console.error(`Error getting autocomplete values for column ${columnName}:`, error);
    return [];
  }
};
